import {
  Collapse,
  Row,
  Col,
  Input,
  InputNumber,
  AutoComplete,
  Tooltip,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';

import { WorkOrderStore } from 'stores';

import styled from 'styled-components';
import {
  InfoCircleOutlined,
  UpOutlined,
  SearchOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

import { clearNumber } from './utils';

const { Panel } = Collapse;
const { Option, OptGroup } = AutoComplete;

const text = 'Some content';

const StyledPanel = styled(Panel)`
  .ant-collapse-header {
    background: white !important;
  }
  .ant-collapse-content {
    background: white !important;
  }
`;

const StyledRow = styled(Row)`
  padding-bottom: 10px;
  padding-left: 41px;
  padding-right: 18px;
`;

const InputsRow = styled(Row)`
  &:hover {
    input {
      border: none;
      box-shadow: none;
    }
  }
  &.focused,
  &:focus-visible {
    input {
      border: none;
      box-shadow: none;
    }
  }
`;

const StyledInput = styled(Input)`
  .ant-input {
    border: none; /* Remove default border */
    box-shadow: none; /* Remove default box-shadow (Ant Design adds this on focus) */
    background: transparent; /* Optional: Remove background if needed */
    transition: border 0.3s ease; /* Smooth transition for border changes */

    /* Add border on hover */
    &:hover {
      border: none;
      box-shadow: none;
    }

    /* Add border on focus */
    &:focus {
      border: none;
      box-shadow: none; /* Override Ant Design's default focus shadow */
    }
  }

  /* If the input has a prefix/suffix, target the wrapper */
  &.ant-input-affix-wrapper {
    border: none; /* Remove default border for the wrapper */
    box-shadow: none;

    padding-left: 1px;
    padding-right: 1px;

    &:hover {
      border: 1px solid #d9d9d9;
    }

    &.ant-input-affix-wrapper-focused {
      border: 1px solid #d9d9d9;
      box-shadow: none;
    }

    .ant-input-prefix {
      margin: 0px;
    }
  }

  /* Optional: Handle disabled state */
  &.ant-input-disabled,
  &.ant-input-affix-wrapper-disabled {
    border: none; /* Ensure no border in disabled state */
    background: #f5f5f5; /* Ant Design's default disabled background */
  }
`;

const StyledInputNumber = styled(InputNumber)`
  input {
    border: none;
    box-shadow: none;
    width: 80%;
    &:hover,
    &:focus,
    &:focus-visible {
      border: 1px solid #d9d9d9;
      box-shadow: none;
    }
  }
`;

const StyledAutoComplete = styled(AutoComplete)`
  & .ant-select-selector {
    border: 1px solid white !important;
    box-shadow: none !important;
    border-radius: 4px;
    transition: border 0.3s ease;

    .ant-select-selection-search-input {
      border: none;
      box-shadow: none;
    }
  }

  &:hover {
    outline: 1px solid #d9d9d9;
    border-radius: 4px;
  }

  &:hover .ant-select-selector {
    border: 1px solid white !important;
  }

  &.ant-select-focused .ant-select-selector {
    border: 1px solid white !important;
    box-shadow: none !important;
  }

  &.ant-select-disabled .ant-select-selector {
    border: 1px solid white !important;
    background: #f5f5f5;
  }

  & .ant-select-dropdown {
    width: 100% !important;
  }
`;

const StyledAutocompleteInput = styled(Input)`
  & #autocomplete-input {
    width: 100% !important;
  }
`;

const StyledIcon = styled(InfoCircleOutlined)`
  color: black !important;
  margin-left: 5px;
  height: 100%;
`;

function LineItem() {
  return (
    <>
      <StyledRow>
        <Col span={12}></Col>
        <Col span={12}>
          <Row>
            {['Labor Cost', 'Parts Cost', 'Misc. Cost', 'Subtotal'].map(
              (label) => (
                <Col span={6} key={label}>
                  {label}:
                </Col>
              )
            )}
          </Row>
        </Col>
      </StyledRow>
      <Collapse
        defaultActiveKey={[]}
        onChange={() => {}}
        accordion
        expandIcon={({ isActive }) => (
          <UpOutlined
            style={{
              color: '#123FBF',
              transform: `rotate(${isActive ? 0 : 90}deg)`,
              transition: 'transform 0.2s',
            }}
          />
        )}
        collapsible="icon"
      >
        <StyledPanel key="1" header={<ItemHeader />}>
          <p>Text</p>
        </StyledPanel>
      </Collapse>
    </>
  );
}

export default LineItem;

export function ItemHeader({
  item,
  onSelectVMRSCode,
  deleteLineItem,
  changeItemProperty,
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [vmrsCodes, setVMRSCodes] = useState([]);
  const [value, setValue] = useState(
    !!item.vmrs_full_code && !!item.vmrs_full_description
      ? `${item.vmrs_full_code} ${item.vmrs_full_description}`
      : ''
  );
  const toggleFocus = (state) => () => setIsFocused(state);

  const handleBlur = (field, value) => {
    const finalValue = parseFloat(value);
    const safeValue = isNaN(finalValue) ? 0 : parseFloat(finalValue.toFixed(2));

    changeItemProperty(item.key, field, safeValue);
    changeItemProperty(item.key, `${field}ChangedManually`, true);
  };

  const { laborTotal, partsTotal, miscTotal } = item;
  const miscsTotal = item.miscellaneous.reduce(
    (total, item) => total + item.quantity * item.unit_cost,
    0
  );

  const miscellaneousTotal = miscTotal || miscsTotal;

  const [localLaborTotal, setLocalLaborTotal] = useState(laborTotal);
  const [localPartsTotal, setLocalPartsTotal] = useState(partsTotal);
  const [localMiscTotal, setLocalMiscTotal] = useState(miscellaneousTotal);

  const total = laborTotal + partsTotal + miscellaneousTotal;

  const handleSelect = (value, option) => {
    let event = option.props.onClick;
    if (event) {
      event = (e) => {
        e.stopPropagation();
      };
    }
    setValue(value);
    onSelectVMRSCode(value);
  };

  useEffect(() => {
    if (!!item.vmrs_full_code && !!item.vmrs_full_description) {
      setValue(`${item.vmrs_full_code} ${item.vmrs_full_description}`);
    } else {
      setValue('');
    }
  }, [item.vmrs_full_code, item.vmrs_full_description]);

  useEffect(() => {
    setLocalLaborTotal(laborTotal);
  }, [laborTotal]);
  useEffect(() => {
    setLocalPartsTotal(partsTotal);
  }, [partsTotal]);
  useEffect(() => {
    setLocalMiscTotal(miscTotal);
  }, [miscTotal]);

  const handleClick = (event) => {
    event.stopPropagation();
  };

  const getVMRSCodes = async (value) => {
    if (!value || value.trim() === '') {
      setVMRSCodes([]);
      return;
    }
    const woOcrAbortController = new AbortController();
    const data = await WorkOrderStore.workOrderVMRS(
      {
        user_search_term: value,
      },
      woOcrAbortController.signal
    );
    setVMRSCodes(data);
  };

  const searchHandler = (value) => {
    setValue(value);
    debounce(() => getVMRSCodes(value), 500)();
  };

  const options = (vmrsCodes || []).map((item) => {
    return (
      <Option key={item.vmrs_code} value={`${item.vmrs_code} ${item.desc}`}>
        {item.vmrs_code}{' '}
        <span className="certain-search-item-count">{item.desc}</span>
      </Option>
    );
  });

  return (
    <Row style={{ width: '100%', marginLeft: '5px' }}>
      <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
        <StyledAutoComplete
          className="certain-category-search"
          dropdownClassName="certain-category-search-dropdown"
          dropdownMatchSelectWidth={true}
          style={{ width: '90%' }}
          dataSource={options}
          placeholder="Search Service Task or VMRS Code"
          optionLabelProp="value"
          onSelect={handleSelect}
          onSearch={searchHandler}
          value={value}
        >
          <StyledAutocompleteInput
            id="autocomplete-input"
            placeholder="Search Service Task or VMRS Code"
            style={{ width: '100%' }}
            suffix={<SearchOutlined />}
          />
        </StyledAutoComplete>
        <Tooltip title="Use the search to find and add VMRS codes for this line item. If the exact code isn’t available, select the closest one and edit the VMRS code(s) as needed.">
          <StyledIcon />
        </Tooltip>
      </Col>
      <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
        <InputsRow
          className={isFocused ? 'focused' : ''}
          style={{ width: '90%' }}
        >
          <Col span={6}>
            <StyledInput
              onClick={(e) => e.stopPropagation()}
              prefix="$"
              value={localLaborTotal}
              onChange={(e) => {
                const cleanedValue = clearNumber(e.target.value);
                setLocalLaborTotal(cleanedValue);
              }}
              onBlur={() => handleBlur('laborTotal', localLaborTotal)}
              onFocus={toggleFocus(true)}
            />
          </Col>
          <Col span={6}>
            <StyledInput
              onClick={(e) => e.stopPropagation()}
              prefix="$"
              value={localPartsTotal}
              onChange={(e) => {
                const cleanedValue = clearNumber(e.target.value);
                setLocalPartsTotal(cleanedValue);
              }}
              onBlur={() => handleBlur('partsTotal', localPartsTotal)}
              onFocus={toggleFocus(true)}
            />
          </Col>
          <Col span={6}>
            <StyledInput
              onClick={(e) => e.stopPropagation()}
              prefix="$"
              value={localMiscTotal}
              onChange={(e) => {
                const cleanedValue = clearNumber(e.target.value);
                setLocalMiscTotal(cleanedValue);
              }}
              onBlur={() => handleBlur('miscTotal', localMiscTotal)}
              onFocus={toggleFocus(true)}
            />
          </Col>
          <Col span={6}>
            <StyledInput
              onClick={(e) => e.stopPropagation()}
              prefix="$"
              value={total.toFixed(2)}
              onFocus={toggleFocus(true)}
              onBlur={toggleFocus(false)}
              readOnly
            />
          </Col>
        </InputsRow>
        <DeleteOutlined
          style={{ fontSize: '20px', color: 'red' }}
          onClick={deleteLineItem}
        />
      </Col>
    </Row>
  );
}
